// Doorframe-transition spec (seamless-world Step 5, `motion-threshold` token).
//
// Pure-data + pure helper so unit tests can lock the phase timings + the
// reduced-motion contract without needing a Phaser camera / scene. The
// Phaser-coupled execution lives in WorldScene.doorTransition; this module
// supplies the numbers + the planning logic.

/** Phase 1 — fade out to black. */
export const DOOR_PHASE1_MS = 180;
/** Phase 2 — black hold while the tilemap swap fires (brief "you're
 *  between rooms" beat; cartridge has this implicit hold during the
 *  blackout). */
export const DOOR_PHASE2_MS = 80;
/** Phase 3 — fade in from black. */
export const DOOR_PHASE3_MS = 180;
/** Composite total — 440 ms, the `motion-threshold` token's value. */
export const DOOR_TOTAL_MS = DOOR_PHASE1_MS + DOOR_PHASE2_MS + DOOR_PHASE3_MS;

/** Fade overlay color — pure black. Cartridge-authentic (FireRed door
 *  transitions are a plain black fade with no camera move) AND harmonizes
 *  with the black interior bg from applyCameraBounds. */
export const DOOR_FADE_RGB: readonly [number, number, number] = [0, 0, 0];

export interface DoorTransitionPlan {
  /** When the tilemap swap fires (ms from transition start). For the
   *  default path: at the END of phase 1 (camera reaches peak zoom +
   *  overlay reaches full opacity). For reduced motion: at t=0. */
  swapAtMs: number;
  /** When `transitioning` toggles back to false (ms from start). */
  resolveAtMs: number;
  /** True if reduced motion. Callers should skip all tween schedules and
   *  fire the swap synchronously. */
  reduced: boolean;
}

/** Build the phase timing plan from a reduced-motion flag. */
export function planDoorTransition(reducedMotion: boolean): DoorTransitionPlan {
  if (reducedMotion) {
    return { swapAtMs: 0, resolveAtMs: 0, reduced: true };
  }
  return {
    swapAtMs: DOOR_PHASE1_MS,
    resolveAtMs: DOOR_TOTAL_MS,
    reduced: false,
  };
}
